import { Inject, Component, OnInit, OnDestroy, ViewChild, ElementRef, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  ChannelUIds,
  MassNotificationTemplate,
  CommunicationTemplate,
  MassNotification,
  MassNotificationRecipient,
  TemplateViewControlType
} from 'src/app/modules/communications/store/communications/communications.model';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { Workflow, WorkflowParentType, WorkflowStepAction } from 'src/app/modules/core/store/models/workflow.model';
import { CommunicationsApiService } from '../../../store/communications/communications-api.service';
import { ChannelSelectionService } from '../../../services/channel-selection-service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { TemplateDesignerService } from '../../../services/template-designer.service';
import { TemplateDesigner } from '../../../components/template/designer/designer/designer.component';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { Document } from 'src/app/modules/core/store/models';
import { FieldToValueMapping } from '@app/modules/communications/components/template/designer/design-elements/design-elements.models';
import { DEFAULT_APP_CULTURE_CODE } from '@app/core/constants';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { UserContext } from '@app/modules/authentication/store/auth';
import moment from 'moment';
import { NotificationStatusService } from '@app/modules/communications/services/notification-status-service';
@Component({
    selector: 'topo-comms-notification-edit',
    templateUrl: './notification-edit.component.html',
    styleUrls: ['./notification-edit.component.scss'],
    standalone: false
})
export class NotificationEditComponent implements OnInit, OnDestroy {
  isActive: boolean;
  isDirty: boolean;
  isDrawerOpen: boolean;
  selectedTabIndex: number = 0;
  mainTabIndex: number = 0;
  recipientWorkflow: Workflow;
  notificationWorkflow: Workflow;
  recipientForDetails: MassNotificationRecipient;
  tabs: string[] = [];
  notification: MassNotification;
  template: MassNotificationTemplate;
  selectedMessageTabIndex: number;
  userContext: UserContext;
  messageTabs: string[] = [];
  messageTabIcons: string[] = [];
  messageTabChannels: { channelUId: string; icon: string; templateUId?: string }[] = [];
  selectedCommunicationTemplate: CommunicationTemplate;
  editableControlTypes = [];
  deletableControlTypes = [];
  selectedCulture: string;
  needToApplyOverrides: boolean;
  dataset: Dataset;
  document: Document;
  documentContext: { dataset: Dataset; document: Document };
  documentMappings: FieldToValueMapping;
  isEmailTemplate: boolean;
  isTemplateTitle: boolean;
  isTemplateSummary: boolean;
  emailSubject: string;
  templateSummary: string;
  @ViewChildren(TemplateDesigner) designers;
  destroy$ = new Subject<void>();

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private channelSelectionService: ChannelSelectionService,
    private templateDesignerService: TemplateDesignerService,
    private cultureService: CultureService,
    private commApiService: CommunicationsApiService,
    private datasetApiService: DatasetApiService,
    private dataApiService: DataApiService,
    private changeDetector: ChangeDetectorRef,
    private notificationStatusService: NotificationStatusService
  ) {}

  ngOnInit(): void {
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-OVERVIEW'));

    this.channelSelectionService.loadAvailableChannels(true);
    this.templateDesignerService.init(false);

    this.store
      .select(fromComm.selectSelectedMassNotification)
      .pipe(withLatestFrom(this.store.select(fromComm.selectIsMassNotificationEditing)), takeUntil(this.destroy$))
      .subscribe(([notification, isEditing]) => {
        const wasActive = this.isActive;
        this.isActive = !!notification && isEditing === false;
        this.notification = notification;
        if (wasActive !== this.isActive) {
          this.isDrawerOpen = true;
          this.mainTabIndex = 0;
          if (this.isActive === true) {
            this.loadDesigns();
          }
        }
        this.changeDetector.markForCheck();
      });

    this.store
      .select(fromComm.selectIsdMassNotificationDirty)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isDirty => {
        this.isDirty = isDirty;
      });

    // called when signalR updates the stats
    this.store
      .select(fromComm.selectSelectedMassNotificationStats)
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        if (!!stats && this.notification?.uId === stats?.notification.uId) {
          if (!!stats?.notification) {
            this.notification = stats.notification;
            this.changeDetector.detectChanges();
          }
        }
      });

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        if (!!userContext) {
          this.userContext = userContext;
        }
      });

    this.channelSelectionService.channelsLoadedEvents$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (!!this.notification && this.template) {
        this.loadDesigns();
      }
    });

    this.notificationStatusService.loadNotificationRatingScale();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.store.dispatch(fromComm.setSelectedMassNotification({ notification: null, isDirty: false }));
  }

  onConfirmClose(response: boolean): void {}

  drawerOpenClose(open: boolean): void {
    this.isDrawerOpen = open;
  }

  setChanged(): void {}

  onDashBoardTabSelected(): void {
    this.mainTabIndex = 0;
  }

  onRecipientsTabSelected(): void {
    this.mainTabIndex = 1;
  }

  onRecipientWorkflowLoaded(workflow: Workflow): void {
    this.recipientWorkflow = workflow;
  }

  onNotificationWorkflowLoaded(workflow: Workflow): void {
    this.notificationWorkflow = workflow;
  }

  onRecipientDetalsRequested(recipient: MassNotificationRecipient): void {
    this.recipientForDetails = recipient;
  }

  onRecipientsDetailsClosed(): void {
    this.recipientForDetails = null;
  }

  onMessageTabSelected(): void {
    this.mainTabIndex = 2;
    this.changeDetector.detectChanges();
    setTimeout(() => {
      this.updateDocumentFieldRuntimeData();
      this.updateDesignerDocumentContext();
      this.changeDetector.detectChanges();
    }, 100);
  }

  private loadDesigns(): void {
    this.messageTabs = [];
    this.messageTabIcons = [];
    this.messageTabChannels = [];
    this.selectedMessageTabIndex = 0;
    this.selectedCulture = this.cultureService?.currentLang;

    this.dataset = null;
    this.document = null;

    const channelUIds = this.channelSelectionService.getUniqueChannelUIds(this.notification.channelsCascadeSettings);

    this.commApiService.getMassNotificationTemplate(this.notification?.notificationTemplateUId).subscribe(template => {
      this.template = template;

      channelUIds?.forEach(channelUId => {
        let commTemplate = this.templateDesignerService.getCommunicationTemplateByChannelUId(this.template, channelUId);

        if (!!commTemplate) {
          this.messageTabs.push(this.channelSelectionService.getChannelName(commTemplate.channelUId));
          this.messageTabChannels.push({
            channelUId: commTemplate.channelUId,
            icon: this.channelSelectionService.getChannelIcon(commTemplate.channelUId),
            templateUId: commTemplate.uId
          });
          this.messageTabIcons.push(this.channelSelectionService.getChannelIcon(commTemplate.channelUId));
        }
      });

      if (this.messageTabs.length) {
        this.onMessageTabChanged(this.selectedMessageTabIndex);
      }
    });

    if (!!this.notification?.datasetUId && !!this.notification?.documentUId) {
      this.loadDatasetAndDocument(this.notification);
    }
  }

  private getNotificationDocUpdateDate(notification: MassNotification): string {
    let cultureCode = this.userContext.cultureCode;
    let docUpdateDate = notification.workflowState?.[`${cultureCode}_docUpdated`];
    if (!!!docUpdateDate) {
      cultureCode = this.userContext.fallbackCultureCode;
      docUpdateDate = notification.workflowState?.[`${cultureCode}_docUpdated`];
      if (!!!docUpdateDate) {
        cultureCode = DEFAULT_APP_CULTURE_CODE;
        docUpdateDate = notification.workflowState?.[`${cultureCode}_docUpdated`];
      }
      if (!!!docUpdateDate) {
        docUpdateDate = notification.workflowState?.['docUpdated'];
      }
      if (!!!docUpdateDate) {
        docUpdateDate = null;
      }
    }
    return docUpdateDate;
  }

  private loadDatasetAndDocument(notification: MassNotification): void {
    this.documentMappings = {};
    const docUpdateDate = this.getNotificationDocUpdateDate(notification);
    if (!!notification?.datasetUId && !!notification?.documentUId) {
      this.datasetApiService.getDataset(notification?.datasetUId, { includeWorkflow: true }).subscribe(dataset => {
        this.dataset = dataset;
        // try to load document at notification created time
        this.dataApiService
          .getDocumentAtTime(dataset.uId, notification?.documentUId, notification.createdOn, docUpdateDate, this.selectedCulture)
          .subscribe(timeDoc => {
            if (!!timeDoc) {
              this.applyLoadedDocument(timeDoc);
            } else {
              // failed, just load the current document
              this.dataApiService.getDocument(dataset.uId, notification?.documentUId).subscribe(doc => {
                this.applyLoadedDocument(doc);
              });
            }
          });
      });
    }
  }

  private applyLoadedDocument(document: Document): void {
    this.document = document;

    this.documentContext = { dataset: this.dataset, document: this.document };
    this.updateDocumentFieldRuntimeData();
    this.updateDesignerDocumentContext();
    if (!!this.dataset && !!this.document) {
      this.documentMappings = this.templateDesignerService.buildDocumentFieldMapping(this.dataset, this.document, this.selectedCulture);
    }
    this.getTitlesAndSummary();
    this.changeDetector.detectChanges();
  }

  private updateDocumentFieldRuntimeData(): void {
    this.designers?.forEach(designer => {
      const fieldControls = designer?.template?.viewControls?.filter(c => c.controlType === TemplateViewControlType.FormField);
      fieldControls?.forEach(c => {
        designer.setRuntimeData(c.uId, this.document);
      });
    });
  }

  private updateDesignerDocumentContext(): void {
    if (!!this.dataset && !!this.document) {
      this.designers?.forEach(designer => {
        designer.setDocumentContext(this.dataset, this.document, this.notification.mapUId);
      });
    }
  }

  private getTitlesAndSummary(): void {
    this.isEmailTemplate =
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.SendGridEmailChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.TopoEmailChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.HorizonMobilePushChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.GwtsMobileChannelUId;

    this.isTemplateTitle =
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.HorizonMobilePushChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.GwtsMobileChannelUId;

    this.isTemplateSummary = this.selectedCommunicationTemplate?.channelUId === ChannelUIds.HorizonMobilePushChannelUId;

    if (this.isEmailTemplate) {
      let subjectValue = this.templateDesignerService.getTranslateText(
        this.selectedCommunicationTemplate?.settings?.emailSubject,
        this.selectedCulture
      );

      let override = this.notification.viewControlOverrides?.find(
        o => o.templateUId === this.selectedCommunicationTemplate.uId && o.controlUId === 'emailSubject'
      );
      if (!!override) {
        subjectValue = this.templateDesignerService.getTranslateText(override.settings, this.selectedCulture);
      }
      if (!!this.document) {
        Object.keys(this.document).forEach(key => {
          const replacement = '{{' + key + '}}';
          if (!!this.documentMappings && !!this.documentMappings[key]) {
            subjectValue = subjectValue?.replace(replacement, this.documentMappings[key].value);
          } else {
            subjectValue = subjectValue?.replace(replacement, '');
          }
        });
      }
      this.emailSubject = subjectValue?.replace(/{{\w+}}/g, '');
    }

    if (this.isTemplateSummary) {
      let summaryValue = this.templateDesignerService.getTranslateText(
        this.selectedCommunicationTemplate?.settings?.summary,
        this.selectedCulture
      );
      let override = this.notification.viewControlOverrides?.find(
        o => o.templateUId === this.selectedCommunicationTemplate.uId && o.controlUId === 'summary'
      );
      if (!!override) {
        summaryValue = this.templateDesignerService.getTranslateText(override.settings, this.selectedCulture);
      }
      if (!!this.document) {
        Object.keys(this.document).forEach(key => {
          const replacement = '{{' + key + '}}';
          if (!!this.documentMappings && !!this.documentMappings[key]) {
            summaryValue = summaryValue?.replace(replacement, this.documentMappings[key].value);
          } else {
            summaryValue = summaryValue?.replace(replacement, '');
          }
        });
      }
      this.templateSummary = summaryValue?.replace(/{{\w+}}/g, '');
    }
  }

  onMessageTabChanged(index: number): void {
    this.selectedMessageTabIndex = index;
    this.selectedCommunicationTemplate = this.template?.communicationTemplates?.find(
      t => t.uId === this.messageTabChannels[index].templateUId
    );

    this.isEmailTemplate =
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.SendGridEmailChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.TopoEmailChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.HorizonMobilePushChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.GwtsMobileChannelUId;

    this.isTemplateTitle =
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.HorizonMobilePushChannelUId ||
      this.selectedCommunicationTemplate?.channelUId === ChannelUIds.GwtsMobileChannelUId;

    this.getTitlesAndSummary();

    setTimeout(() => {
      this.updateDocumentFieldRuntimeData();
      this.updateDesignerDocumentContext();
      this.changeDetector.detectChanges();
    }, 100);
  }

  public getChartWidth(): string {
    const diff = this.isDrawerOpen ? 420 + 450 + 108 : 420 + 156;
    return `calc((99vw - ${diff}px)/2)`;
  }

  public getBarWidth(): string {
    const diff = this.isDrawerOpen ? 420 + 450 + 48 : 420 + 100;
    return `calc(99vw - ${diff}px)`;
  }

  onNotificationNavigate(action: WorkflowStepAction): void {
    const destStep = this.notificationWorkflow?.workflowSteps?.find(s => s.uId === action.destinationStepUId);

    const updatedNotification = {
      ...this.notification,
      workflowStepUId: destStep.uId
    };

    this.store.dispatch(fromComm.updateMassNotification({ notification: updatedNotification, close: false }));
  }
}
